import {
  DE_SLOPING_FLOW,
  DE_SLOPING_WITH_OPERATIONS_FLOW,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/de-sloping/utils";
import {
  DEBUNKERING_FLOW,
  DEBUNKERING_WITH_OPERATIONS_FLOW,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/debunkering/utils";
import {
  HSE_WASTE_DISPOSAL_SLOPS_FLOW,
  isHSEWasteDisposalSlops,
  isHSEWasteDisposalSludge,
  WASTE_DISPOSAL_SLUDGE_FLOW,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/utils";
import { approveByMDSlopsDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approveByMDSlopsDisposalSchema";
import { approveByOperationsBunkeringSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approveByOperationsBunkeringSchema";
import { approveByOperationsGarbageDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approveByOperationsGarbageDisposalSchema";
import { approvedByMDGarbageDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approvedByMDGarbageDisposalSchema";
import { closeBunkeringSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/closeBunkeringSchema";
import { closeGarbageDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/closeGarbageDisposalSchema";
import { closeSlopsDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/closeSlopsDisposalSchema";
import { rejectionSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/rejectionSchema";
import { startGarbageDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/startGarbageDisposalSchema";
import { startSlopsDisposalSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/startSlopsDisposalSchema";
import {
  isBunkering,
  isDebunkering,
  isDeSloping,
  isWasteDisposalGarbage,
  isWasteDisposalSlops,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import { isShipChandlingLifeboatsWorkflowService } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import type { MarpolAnnexI } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import {
  isLoadingAndOffloadingType,
  SHIP_CHANDLING_LIFEBOATS_FLOW,
  SHIP_CHANDLING_LOADING_AND_OFFLOADING_FLOW,
} from "../ship-chandling/utils";
import { isShipToShipType, SHIP_TO_SHIP_FLOW } from "../ship-to-ship/utils";

export interface CancelRule {
  userRoles?: string[];
  permission: string;
}

export interface PortServiceFlowStatus {
  label: string;
  badgeType: string;
  canBeSelected: boolean;
  actionPermission: string | null;
  extraFields?: string[];
}

export interface PortServiceFlowStep {
  serviceRequester: string;
  canApprove: string[];
  canReject: string[];
  cannotReject?: string[];
  cannotChangeStatusRoles?: string[];
  nextStatus: string;
  nonApproverStatus: string;
  defaultNotifyRoles: string[];
  defaultNotifyRolesByStatus?: Record<string, string[]>;
  shouldAppendNotifyRoles?: boolean;
  statusesList: Record<string, PortServiceFlowStatus>;
  canCancel?: CancelRule[];
}

export type PortServiceFlow = Record<string, PortServiceFlowStep>;

export const NON_SHIPPING_AGENT_REJECT_ROLE = "ALL_EXCEPT_SHIPPING_AGENT";

const MARINE_DEPARTMENT_NOTIFY_ROLES = [
  "Marine Operator",
  "Marine Supervisor",
  "Marine Office",
  "Harbour Master",
];

const MARINE_DEPARTMENT_ROLE_KEYS = [
  "marine-operator",
  "marine-supervisor",
  "marine-office",
  "harbour-master",
];

const OPERATIONS_ROLE_KEYS = ["operations", "terminal"];

const OPERATIONS_NOTIFY_ROLES = ["Dry Bulk", "Operations"];

type NotifyRoleValue = string | { label?: string; value?: string };

const getNotifyRoleValue = (role: NotifyRoleValue) =>
  typeof role === "string" ? role : (role.value ?? role.label ?? "");

export const hasOperationsNotifyRole = (notifyRoles: NotifyRoleValue[]) =>
  notifyRoles.some((role) => {
    const value = getNotifyRoleValue(role);

    return OPERATIONS_NOTIFY_ROLES.includes(value) || value.endsWith("_OPERATIONS");
  });

export const canRejectPortServiceFlowStep = (
  flowStep: PortServiceFlowStep | null | undefined,
  currentUserType: string,
) => {
  if (!flowStep) return false;

  if (flowStep.cannotReject?.includes(currentUserType)) {
    return false;
  }

  if (flowStep.canReject.includes(NON_SHIPPING_AGENT_REJECT_ROLE)) {
    return currentUserType !== "Shipping Agent";
  }

  return flowStep.canReject.includes(currentUserType);
};

export const canRejectWithoutActionPermission = (
  flowStep: PortServiceFlowStep | null | undefined,
) => !!flowStep?.canReject.includes(NON_SHIPPING_AGENT_REJECT_ROLE);

export const isUserRoleExcludedFromFlowStep = ({
  cannotChangeStatusRoles,
  currentUserRoles,
}: {
  cannotChangeStatusRoles?: string[];
  currentUserRoles: string[];
}) =>
  !!cannotChangeStatusRoles?.some((excludedUserRole) =>
    currentUserRoles.includes(excludedUserRole),
  );

export const WASTE_DISPOSAL_GARBAGE_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: ["Operations", "MD"],
    nextStatus: STATUS_KEYS.OPERATIONS_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Marine Operator", "Marine Supervisor"],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: {
        label: "Approved by Operations",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesWasteDisposalGarbage",
        extraFields: ["actualNumberOfSkips"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesWasteDisposalGarbage",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.OPERATIONS_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["Operations", "MD"],
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesWasteDisposalGarbage",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesWasteDisposalGarbage",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: ["Operations", "MD"],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortServicesWasteDisposalGarbage",
        extraFields: ["ats", "etc"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesWasteDisposalGarbage",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    canCancel: [
      {
        userRoles: ["marine-operator", "marine-supervisor", "operations"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortServicesWasteDisposalGarbage",
        extraFields: [
          "atc",
          "actualQuantity",
          "actualNumberOfSkips",
          "additionalNumberOfSkips",
          "garbageDisposalDocument",
        ],
      },
    },
  },
};

export const WASTE_DISPOSAL_SLOPS_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["MD"],
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesWasteDisposalSlops",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesWasteDisposalSlops",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Marine Operator", "Marine Supervisor"],
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortServicesWasteDisposalSlops",
        extraFields: ["ats", "etc", "ets"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    canCancel: [
      {
        userRoles: ["marine-operator", "marine-supervisor"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortServicesWasteDisposalSlops",
        extraFields: [
          "atc",
          "wasteResiduesForDischargeFacility",
          "slopsDisposalDocuments",
          "serviceReportAttachment",
        ],
      },
    },
  },
};

export const BUNKERING_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["MD"],
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortService",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    cannotReject: ["MD"],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortService",
        extraFields: ["ats", "ets", "etc"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: MARINE_DEPARTMENT_NOTIFY_ROLES,
    canCancel: [
      {
        userRoles: MARINE_DEPARTMENT_ROLE_KEYS,
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortService",
        extraFields: ["atc", "actualQuantity", "serviceReportAttachment"],
      },
    },
  },
};

export const BUNKERING_WITH_OPERATIONS_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: BUNKERING_FLOW[STATUS_KEYS.PENDING_APPROVAL],
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: ["Operations"],
    cannotReject: ["MD"],
    nextStatus: STATUS_KEYS.OPERATIONS_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: ["Shipping Agent"],
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: {
        label: "Approved by Operations",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortService",
        extraFields: ["notes", "attachment"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.OPERATIONS_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    cannotReject: ["MD", "Operations"],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortService",
        extraFields: ["ats", "ets", "etc"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    ...BUNKERING_FLOW[STATUS_KEYS.STARTED],
    canCancel: [
      {
        userRoles: [...MARINE_DEPARTMENT_ROLE_KEYS, ...OPERATIONS_ROLE_KEYS],
        permission: "canCancelPortService",
      },
    ],
  },
};

export const validateGarbageDisposalExtraFields = ({
  newStatusOption,
  notifyOption,
  actualNumberOfSkips,
  additionalNumberOfSkips,
  internalNotes,
  externalNotes,
  rejectionReason,
  flowAts,
  flowEtc,
  flowAtc,
  actualQuantity,
  garbageDisposalDocument,
  isBerth,
}: {
  newStatusOption: string;
  notifyOption: string[];
  actualNumberOfSkips?: number;
  additionalNumberOfSkips?: number;
  internalNotes: string;
  externalNotes: string;
  rejectionReason: string;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  actualQuantity?: number;
  garbageDisposalDocument: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  isBerth?: boolean;
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.OPERATIONS_APPROVED:
      return approveByOperationsGarbageDisposalSchema().safeParse({
        actualNumberOfSkips,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.MD_APPROVED:
      return approvedByMDGarbageDisposalSchema().safeParse({
        internalNotes,
        externalNotes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startGarbageDisposalSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeGarbageDisposalSchema(isBerth, flowAts).safeParse({
        atc: flowAtc,
        actualQuantity,
        actualNumberOfSkips,
        additionalNumberOfSkips,
        notificationOption: notifyOption,
        garbageDisposalDocument,
      });

    default:
      return null;
  }
};

export const validateSlopsDisposalExtraFields = ({
  newStatusOption,
  notifyOption,
  internalNotes,
  externalNotes,
  rejectionReason,
  flowAts,
  flowEtc,
  actualQuantity,
  actualNumberOfSkips,
  flowAtc,
  flowEts,
  slopsDisposalDocuments,
  serviceReportAttachment,
  wasteResiduesForDischargeFacility,
}: {
  newStatusOption: string;
  notifyOption: string[];
  internalNotes: string;
  externalNotes: string;
  rejectionReason: string;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  flowEts: string | Date | null;
  actualQuantity?: number;
  actualNumberOfSkips?: number;
  slopsDisposalDocuments: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }[];
  serviceReportAttachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  wasteResiduesForDischargeFacility?: MarpolAnnexI[];
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.MD_APPROVED:
      return approveByMDSlopsDisposalSchema().safeParse({
        internalNotes,
        externalNotes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startSlopsDisposalSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        ets: flowEts,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeSlopsDisposalSchema(flowAts).safeParse({
        atc: flowAtc,
        actualQuantity,
        actualNumberOfSkips,
        slopsDisposalDocuments,
        serviceReportAttachment,
        notificationOption: notifyOption,
        wasteResiduesForDischargeFacility,
      });

    default:
      return null;
  }
};

export const validateBunkeringExtraFields = ({
  newStatusOption,
  notifyOption,
  internalNotes,
  externalNotes,
  rejectionReason,
  notes,
  attachment,
  flowAts,
  flowEtc,
  flowAtc,
  actualQuantity,
  serviceReportAttachment,
}: {
  newStatusOption: string;
  notifyOption: string[];
  internalNotes: string;
  externalNotes: string;
  rejectionReason: string;
  notes: string;
  attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  actualQuantity?: number;
  serviceReportAttachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.MD_APPROVED:
      return approveByMDSlopsDisposalSchema().safeParse({
        internalNotes,
        externalNotes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.OPERATIONS_APPROVED:
      return approveByOperationsBunkeringSchema().safeParse({
        notes,
        attachment,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startGarbageDisposalSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeBunkeringSchema(flowAts).safeParse({
        atc: flowAtc,
        actualQuantity,
        serviceReportAttachment,
        notificationOption: notifyOption,
      });

    default:
      return null;
  }
};

export const getPortServiceFlow = (
  category: string,
  type: string,
  subTypeOrNeedsOperationsApproval?: string | boolean,
  needsOperationsApproval?: boolean,
): PortServiceFlow | null => {
  const subType =
    typeof subTypeOrNeedsOperationsApproval === "boolean"
      ? ""
      : (subTypeOrNeedsOperationsApproval ?? "");
  const shouldUseOperationsFlow =
    typeof subTypeOrNeedsOperationsApproval === "boolean"
      ? subTypeOrNeedsOperationsApproval
      : needsOperationsApproval;

  switch (true) {
    case isWasteDisposalGarbage(category, type):
      return WASTE_DISPOSAL_GARBAGE_FLOW;
    case isHSEWasteDisposalSludge(category, type, subType):
      return WASTE_DISPOSAL_SLUDGE_FLOW;
    case isHSEWasteDisposalSlops(category, type, subType ?? ""):
      return HSE_WASTE_DISPOSAL_SLOPS_FLOW;
    case isWasteDisposalSlops(category, type):
      return WASTE_DISPOSAL_SLOPS_FLOW;
    case isShipToShipType(category, type):
      return SHIP_TO_SHIP_FLOW;
    case isShipChandlingLifeboatsWorkflowService(category, type):
      return SHIP_CHANDLING_LIFEBOATS_FLOW;
    case isBunkering(category):
      return shouldUseOperationsFlow ? BUNKERING_WITH_OPERATIONS_FLOW : BUNKERING_FLOW;
    case isDebunkering(category):
      return shouldUseOperationsFlow ? DEBUNKERING_WITH_OPERATIONS_FLOW : DEBUNKERING_FLOW;
    case isDeSloping(category):
      return shouldUseOperationsFlow ? DE_SLOPING_WITH_OPERATIONS_FLOW : DE_SLOPING_FLOW;
    case isLoadingAndOffloadingType(category, type):
      return SHIP_CHANDLING_LOADING_AND_OFFLOADING_FLOW;
    default:
      return null;
  }
};

export const getDefaultNotifyRolesForFlowStep = (
  flowStep: PortServiceFlowStep | null,
  newStatusOption: string,
  shouldAppendNotifyRoles?: boolean,
): string[] => {
  if (!flowStep) return [];

  const defaultRoles = flowStep.defaultNotifyRoles ?? [];
  const statusBasedRoles = flowStep.defaultNotifyRolesByStatus?.[newStatusOption] ?? [];

  if (shouldAppendNotifyRoles) {
    return [...new Set([...defaultRoles, ...statusBasedRoles])];
  }

  return defaultRoles;
};
